import React, { useEffect, useState } from 'react';
import { IntlProvider, FormattedMessage } from 'react-intl';
import {
  arrayOf,
  element,
  shape,
  string,
} from 'prop-types';
import { fromJS } from 'immutable';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import {
  Button,
  ProgressCircular,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Heading,
} from '@americanexpress/dls-react';
import { IconSuccess } from '@americanexpress/dls-icons';
import { useReactlytics } from '@americanexpress/reactlytics';
import { RenderModule } from 'holocron';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Disclaimer, TermsLinks } from '@americanexpress/axp-benefits-utils/components';
import { useGetBestTrackers } from '@americanexpress/axp-benefits-utils/hooks';
import BrowseBenefits from './BrowseBenefits';
import TrackBenefits from './TrackBenefits';
import styles from './AxpBenefitsActivity.scss';
import useVisibilityTracker from './useVisibilityTracker';
import { loadModuleData } from './ducks/loader';
import {
  amexAssuranceDisclosures,
  browseBenefits,
  benefitsTerms,
  benefitsYouCanEnrollIn,
  enrollableStatus,
  MODULE_NAME,
  viewBenefitsActivity,
  BROWSE_BENEFITS_TAB_ID,
  VIEW_BENEFITS_ACTIVITY_TAB_ID,
} from '../constants';
import { getInternalBenefits, getExternalBenefits } from '../utils/utils';

export const getDefaultTabIdFromPath = () => {
  if (typeof window !== 'undefined' && window.location && window.location.pathname.includes('card-benefits/activity')) {
    return VIEW_BENEFITS_ACTIVITY_TAB_ID;
  }

  return BROWSE_BENEFITS_TAB_ID;
};

const AxpBenefitsActivity = (props) => {
  const { track } = useReactlytics();
  const {
    benefitsForActivity,
    cardName = 'platinum',
    categorizedBenefits,
    languageData,
    locale,
    productDisclaimer,
    viewAll,
    viewAllBenefits,
    currentPathname,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState(getDefaultTabIdFromPath);
  const [isHighlightImpressionFired, setIsHighlightImpressionFired] = useState(false);
  const isActivityTabSelected = selectedTab === VIEW_BENEFITS_ACTIVITY_TAB_ID;

  // Sync selectedTab when the pathname changes externally (e.g. via router.replace() in view-all)
  useEffect(() => {
    if (!currentPathname) return;
    if (currentPathname.includes('card-benefits/activity')) {
      setSelectedTab(VIEW_BENEFITS_ACTIVITY_TAB_ID);
    } else if (currentPathname.includes('card-benefits/view-all')) {
      setSelectedTab(BROWSE_BENEFITS_TAB_ID);
    }
  }, [currentPathname]);

  // TODO: Move the API calls to the axp-benefits-view-all module
  // This is a temporary fix so the ReadBestLoyaltyBenefitsTrackers will be cached
  // eslint-disable-next-line no-unused-vars -- temporary
  const { data } = useGetBestTrackers({
    limit: 'ALL',
    locale,
  }, { moduleId: MODULE_NAME });

  /* istanbul ignore next */
  const onToggleExpand = () => {
    setIsExpanded((prevState) => !prevState);
    track({
      event: {
        element: `${cardName}>>enrollment-center>>${isExpanded ? 'view-less' : 'view-more'}`,
        type: 'click',
      },
    });
  };

  const amexEnrollmentComponent = useVisibilityTracker('amex-enrollment-component', (label) => {
    /* istanbul ignore next */
    track({
      event: {
        element: `${cardName}>>${label}`,
        type: 'impression',
      },
    });
  });

  const disclaimerRef = useVisibilityTracker('disclaimer', (label) => {
    if (selectedTab === VIEW_BENEFITS_ACTIVITY_TAB_ID) {
      track({
        event: {
          element: `${cardName}>>${label}`,
          type: 'impression',
        },
      });
    }
  });

  useEffect(() => {
    if (!isHighlightImpressionFired && selectedTab === BROWSE_BENEFITS_TAB_ID) {
      track({
        event: {
          element: `${cardName}>>highlights-component`,
          type: 'impression',
        },
      });
      setIsHighlightImpressionFired(true);
    }
  }, [cardName, selectedTab]);

  // Trigger route updates whenever selectedTab changes
  useEffect(() => {
    switch (selectedTab) {
      case VIEW_BENEFITS_ACTIVITY_TAB_ID:
        if (window.location && window.location.pathname !== '/card-benefits/activity') {
          window.history.replaceState(null, '', '/card-benefits/activity');
        }
        break;
      case BROWSE_BENEFITS_TAB_ID:
        if (window.location && window.location.pathname !== '/card-benefits/view-all') {
          window.history.replaceState(null, '', '/card-benefits/view-all');
        }
        break;
      default:
        break;
    }
  }, [selectedTab]);

  if (languageData && Object?.entries(languageData)?.length === 0) {
    return <ProgressCircular data-testid="progress-circular" />;
  }

  const internalBenefits = getInternalBenefits(benefitsForActivity);
  const externalBenefits = getExternalBenefits(viewAllBenefits);

  // Only count benefits that are actually enrollable (excludes NEITHER/EXTERNAL
  // origin types and already-ENROLLED benefits, per getInternalBenefits)
  const hasAnyEnrollable = internalBenefits.some((benefits) => enrollableStatus
    .includes(benefits?.enrollmentApplication?.enrollmentStatus));

  // Show expand button only when collapsing actually hides benefits:
  // 1. More than 2 internal benefits (collapsed view truncates to 2)
  // 2. Partner section is hidden when collapsed (internal >= 2 hides it)
  // 3. Partner section is visible but has >2 external (collapsed truncates to 2)
  const shouldShowExpandButton = internalBenefits.length > 2
    || (externalBenefits.length > 0 && internalBenefits.length >= 2)
    || (externalBenefits.length > 2 && internalBenefits.length < 2);

  const renderQuickView = ({
    benefit,
    benefitsDataForQuickView,
    benefitNamesListFromActivity,
    toggleModal,
    trackerData,
    onEnrollCTA,
  }) => (
    <RenderModule
      moduleName="axp-benefits-quick-view"
      props={{
        languageData,
        locale,
        name: benefit?.benefitName,
        benefitsData: benefitsDataForQuickView,
        closeButtonLabel: 'Close',
        dismissible: true,
        benefitNamesList: benefitNamesListFromActivity,
        cardname: cardName,
        modalDialogRef: null,
        renderFrom: 'activity',
        hideCarousel: true,
        toggleModal,
        trackerData,
        onEnrollCTA,
      }}
    />
  );

  return (
    <div data-testid="layout-container">
      <IntlProvider data-testid="provider-container" locale={locale} messages={languageData}>
        <div className="margin-1-b" data-toggle="tabs">
          <Tabs
            id="activityTabs"
            activationType="automatic"
            selectedTabId={selectedTab}
            onSelectedTabIdChange={(firstArg, secondArg) => {
              // DLS Tabs API shape can vary; support both `(id)` and `(_, id)` callback signatures.
              const nextSelectedTab = typeof firstArg === 'string' ? firstArg : secondArg;
              if (nextSelectedTab) {
                setSelectedTab(nextSelectedTab);
              }
            }}
          >
            <TabList>
              <Tab
                key="tab-browse-benefits"
                id="tab-browse-benefits"
                data-testid="tab-browse-benefits"
                className={styles.minWidth50}
                reactlytics={{ element: `${cardName}>>tab>>browse-benefits`, type: 'click', meta: 'browse benefits' }}
              >
                {browseBenefits}
              </Tab>
              <Tab
                key="tab-view-benefits-activity"
                id="tab-view-benefits-activity"
                data-testid="tab-view-benefits-activity"
                className={`${styles.minWidth50MinusPixels} ${!hasAnyEnrollable ? 'radius-container-sm-b' : ''}`}
                reactlytics={{ element: `${cardName}>>tab>>view-benefits-activity`, type: 'click', meta: 'View Benefits Activity' }}
              >
                {viewBenefitsActivity}
              </Tab>
            </TabList>
            <TabPanel
              aria-labelledby="tab-browse-benefits"
            >
              {viewAll}
            </TabPanel>
            <TabPanel
              aria-labelledby="tab-view-benefits-activity"
              className={`pad-3${!hasAnyEnrollable ? ' display-none' : ''}`}
              title={(
                <div className={styles.benefitsHeaderRow}>
                  <div ref={amexEnrollmentComponent} id="highlightsDivCatalog">
                    <Heading
                      level={2}
                      variant="sans-medium-bold"
                      className="color-text-brand-alt pad-6 margin-1-b"
                    >
                      {benefitsYouCanEnrollIn}
                    </Heading>
                  </div>
                  {shouldShowExpandButton && (
                    <Button
                      variant="tertiary"
                      className={styles.buttonExpand}
                      data-testid="expand-button"
                      onClick={onToggleExpand}
                    >
                      {isExpanded ? 'View Less' : 'View More'}
                    </Button>
                  )}
                </div>
              )}
              subtitle={(
                <TermsLinks
                  cardname={cardName}
                  benefitTermsLabel={benefitsTerms}
                  assuranceDisclosuresLabel={amexAssuranceDisclosures}
                  className="margin-0 margin-3-b flex flex-wrap flex-align-center"
                />
              )}
            >
              {hasAnyEnrollable && (
                <BrowseBenefits
                  benefitsForActivity={benefitsForActivity}
                  viewAllBenefits={viewAllBenefits}
                  categorizedBenefits={categorizedBenefits}
                  cardName={cardName}
                  isExpanded={isExpanded}
                  onToggleExpand={onToggleExpand}
                  renderQuickView={renderQuickView}
                />
              )}
            </TabPanel>
          </Tabs>
          {isActivityTabSelected && (
            <TrackBenefits
              benefitsForActivity={benefitsForActivity}
              viewAllBenefits={viewAllBenefits}
              cardName={cardName}
              languageData={languageData}
              locale={locale}
              categorizedBenefits={categorizedBenefits}
              renderQuickView={renderQuickView}
            />
          )}
          {!hasAnyEnrollable && (
            <div className="card card-rounded pad-3 margin-2-t" data-testid="no-benefits-enrolled">
              <div ref={amexEnrollmentComponent} id="highlightsDivCatalog">
                <Heading
                  level={2}
                  variant="sans-medium-bold"
                  className="color-text-brand-alt pad-6 margin-1-b"
                >
                  {benefitsYouCanEnrollIn}
                </Heading>
              </div>
              <TermsLinks
                cardname={cardName}
                benefitTermsLabel={benefitsTerms}
                assuranceDisclosuresLabel={amexAssuranceDisclosures}
                className="margin-0 margin-3-b flex flex-wrap flex-align-center"
              />
              <div className="margin-3-tb flex flex-column flex-align-center">
                <IconSuccess color="success" size="xl" title="success" />
                <Heading
                  level={3}
                  variant="sans-xsmall-bold"
                  className="color-text-regular margin-2-tb"
                >
                  <FormattedMessage
                    id="zero-benefits"
                    defaultMessage="You enrolled in all of your available benefits."
                  />
                </Heading>
              </div>
            </div>
          )}
          {productDisclaimer && (
            <div
              ref={disclaimerRef}
              id="benefits-disclaimer"
              className="card card-rounded pad-responsive stack flex flex-column flex-justify-center text-nowrap margin-0-lr margin-1-b margin-2-t"
            >
              <Disclaimer
                title={<FormattedMessage id="view-all.disclaimer.title" defaultMessage="Disclaimer" />}
                body={productDisclaimer}
                headingVariant="sans-xsmall-bold"
              />
            </div>
          )}
        </div>
      </IntlProvider>
    </div>
  );
};

export const TestableAxpBenefitsActivity = AxpBenefitsActivity;

export function mapStateToProps(state) {
  return {
    currentPathname: fromJS(state).getIn(['routing', 'locationBeforeTransitions', 'pathname'])
      || (typeof window !== 'undefined' ? window.location.pathname : ''),
  };
}

AxpBenefitsActivity.propTypes = {
  languageData: shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: string.isRequired,
  productDisclaimer: string,
  viewAll: element,
  benefitsForActivity: shape({}).isRequired,
  categorizedBenefits: arrayOf(shape({})),
  cardName: string,
  viewAllBenefits: shape({}),
  currentPathname: string,
};

AxpBenefitsActivity.holocron = {
  name: MODULE_NAME,
  loadModuleData,
};

const hocChain = compose(
  oneAppModuleWrapper(MODULE_NAME),
  connect(mapStateToProps)
);

export default hocChain(AxpBenefitsActivity);
